<template lang="pug">
.add-gym-media(ref="root")
  StepHeader(
    :title="`Step ${step}: Provide Gym Details & Media`"
    description="Add detailed information about your gym by uploading images or videos, adding a description, and including social links.")
  Field(:label="`Add Your Gym Images/Videos (${mediaCount}/${MAX_MEDIA_COUNT})`" :errors="mediaCount > MAX_MEDIA_COUNT ? [`Too many files. Please remove ${mediaCount - MAX_MEDIA_COUNT} to continue.`] : []")
  .gym-media(v-if="uploadMeta.length || mediaMeta.length")
    .gym-image(v-for="(meta, index) in mediaMeta")
      video(v-if="meta.isVideo" :src="meta.src")
      img(v-else :src="meta.src" :alt="__('Uploaded gym image {n}', { n: String(index + 1) })")
      .duration(v-if="meta.duration") {{meta.duration}}
      Icon.remove-img(name="x" fill="white" @click.native="removeMedia(index, $event)")
    .gym-image(v-for="(meta, index) in uploadMeta")
      video(v-if="meta.isVideo" :src="meta.src")
      img(v-else :src="meta.src" :alt="__('Uploaded gym image {n}', { n: String(index + 1) })")
      .duration(v-if="meta.duration") {{meta.duration}}
      Icon.remove-img(name="x" fill="white" @click.native="removeUpload(index, $event)")
  DragAndDropFileUpload(
    icon="misc/camera-plus"
    :button-text="'Upload a file'"
    :loading="false"
    :show-button="false"
    :accept="'.jpg,.jpeg,.png,.gif,.mpg,.mov,.wmv,.mp4'"
    @fileUpload="$emit('uploadMedia', $event)"
    :disabled="mediaCount >= MAX_MEDIA_COUNT"
    :resize="{width:1400}"
  )
  .accepted-file-types {{ __('Accepted file types are .jpg, .png, .mov, or .mp4 with a max size of 10MB') }}
  .logo-preview(v-if="logoSrc")
    img.logo-img(:src="logoSrc" :alt="__('Gym logo')")
    Icon.clear-logo(name="x" fill="white" @click.native="$emit('clearLogo')")
  DragAndDropFileUpload(
    button-text="Upload Gym Logo/Featured Image"
    :loading="false"
    :show-button="true"
    :accept="'.jpg,.jpeg,.png'"
    @fileUpload="$emit('uploadLogo', $event)"
    :disabled="!!logoSrc"
    :resize="{width:420}"
  )
  RogueTextarea(
    label="Gym Description"
    v-model="modelValue.description"
    :placeholder="__('Describe your gym here, including facilities, equipment, and special features.')"
    :maxlength="750"
  )
  RogueInput.social-link(
    label="Website URL"
    name="website"
    placeholder="Enter your gym's website link"
    :modelValue="website.url"
    @update:modelValue="updateSocialLink($event, 'website')"
    :maxlength="100"
  )
  RogueInput.social-link(
    label="Facebook"
    name="facebook"
    placeholder="Enter your gym's Facebook link"
    :modelValue="facebook.url"
    @update:modelValue="updateSocialLink($event, 'facebook')"
    :validateFn="validateFacebook"
    :maxlength="100"
  )
  RogueInput.social-link(
    label="YouTube"
    name="youtube"
    placeholder="Enter your gym's YouTube link"
    :modelValue="youtube.url"
    @update:modelValue="updateSocialLink($event, 'youtube')"
    :validateFn="validateYouTube"
    :maxlength="100"
  )
  RogueInput.social-link(
    label="Instagram"
    name="instagram"
    placeholder="Enter your gym's Instagram link"
    :modelValue="instagram.url"
    @update:modelValue="updateSocialLink($event, 'instagram')"
    :validateFn="validateInstagram"
    :maxlength="100"
  )
  RogueInput.social-link(
    label="TikTok"
    name="tiktok"
    placeholder="Enter your gym's TikTok link"
    :modelValue="tiktok.url"
    @update:modelValue="updateSocialLink($event, 'tiktok')"
    :validateFn="validateTikTok"
    :maxlength="100"
  )
  RogueInput.social-link(
    label="X"
    name="x"
    placeholder="Enter your gym's X link"
    :modelValue="x.url"
    @update:modelValue="updateSocialLink($event, 'x')"
    :validateFn="validateX"
    :maxlength="100"
  )
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import type { IGymLink, IGym } from '@rogue/universal'
import { getCloudinaryUrl } from 'sisu/js/helpers'
import StepHeader from './StepHeader.vue'
import { validate } from '@/js/helpers/validate-fns'

const props = defineProps<{
  modelValue: Partial<IGym>,
  logoSrc?: string,
  uploadMeta: { src: string, isVideo: boolean, duration?: string }[]
  step: number,
}>()

const emit = defineEmits<{(e: 'update:modelValue', gym: Partial<IGym>): void, (e: 'uploadLogo', file: File): void, (e: 'clearLogo'): void, (e: 'uploadMedia', file: File): void, (e: 'removeUpload', index: number): void }>()

const root = ref<HTMLElement>()
const mediaMeta = ref<{ src: string, isVideo: boolean, duration?: string }[]>([])

function findLinkByType(type: string): IGymLink {
  return props.modelValue.social?.find(link => link.type === type) || {
    url: '',
    type,
  }
}

const website = computed<IGymLink>(() => findLinkByType('website'))
const facebook = computed<IGymLink>(() => findLinkByType('facebook'))
const instagram = computed<IGymLink>(() => findLinkByType('instagram'))
const tiktok = computed<IGymLink>(() => findLinkByType('tiktok'))
const x = computed<IGymLink>(() => findLinkByType('x'))
const youtube = computed<IGymLink>(() => findLinkByType('youtube'))

const MAX_MEDIA_COUNT = 6
const mediaCount = computed<number>(() => props.uploadMeta.length + mediaMeta.value.length)

function removeMedia(index: number, event: Event) {
  event?.preventDefault()
  const updatedGym: Partial<IGym> = Object.assign({}, props.modelValue)
  updatedGym.media?.splice(index, 1)
  mediaMeta.value.splice(index, 1)
  emit('update:modelValue', updatedGym)
}

function removeUpload(index: number, event: Event) {
  event?.preventDefault()
  emit('removeUpload', index)
}

function updateSocialLink(url: string, type: string) {
  const updatedGym: Partial<IGym> = Object.assign({}, props.modelValue)
  if (!updatedGym.social) {
    updatedGym.social = []
  }

  const match = updatedGym.social.find(link => link.type === type)
  if (match) {
    match.url = url
  } else {
    updatedGym.social.push({ url, type })
  }

  emit('update:modelValue', updatedGym)
}

onMounted(() => {
  (props.modelValue.media || []).forEach((media, index) => {
    const src = getCloudinaryUrl(media.src)
    const isVideo = media.type.includes('video')
    const duration = isVideo ? '' : undefined
    if (isVideo) {
      const video = document.createElement('video')
      video.preload = 'metadata'

      video.onloadedmetadata = () => {
        const seconds = video.duration
        const hours = Math.floor(seconds / 3600)
        const minutes = Math.floor((seconds % 3600) / 60)
        const remainingSeconds = Math.round(seconds % 60)
        const formattedTime = `${hours > 0 ? `${hours.toString()}:` : ''}${minutes.toString().padStart(1, '0')}:${remainingSeconds.toString().padStart(2, '0')}`

        mediaMeta.value[index].duration = formattedTime
      }

      video.src = src
    }
    mediaMeta.value.push({ src, isVideo, duration })
  })
})

function validateFacebook(val: string) {
  const regex = /^((https|http):\/\/)?(?:www\.)?(facebook|fb)\.(com|me)\/.*$/
  if (val && !val.match(regex)) {
    return 'Invalid Facebook URL'
  }

  return null
}

function validateYouTube(val: string) {
  const regex = /^((https|http):\/\/)?(?:www\.)?youtube\.com\/.*$/
  if (val && !val.match(regex)) {
    return 'Invalid YouTube URL'
  }

  return null
}

function validateInstagram(val: string) {
  const regex = /^((https|http):\/\/)?(?:www\.)?instagram\.com\/.*$/
  if (val && !val.match(regex)) {
    return 'Invalid Instagram URL'
  }

  return null
}

function validateTikTok(val: string) {
  const regex = /^((https|http):\/\/)?(?:www\.)?tiktok\.com\/.*$/
  if (val && !val.match(regex)) {
    return 'Invalid TikTok URL'
  }

  return null
}

function validateX(val: string) {
  const regex = /^((https|http):\/\/)?(?:www\.)?(x|twitter)\.com\/.*$/
  if (val && !val.match(regex)) {
    return 'Invalid X URL'
  }

  return null
}

function validateSocialLinks(): boolean {
  return !!(root.value && validate(root.value))
}

const isValid = computed<boolean>(() => mediaCount.value <= MAX_MEDIA_COUNT)

defineExpose({
  isValid,
  validate: validateSocialLinks,
})

</script>

<style lang="scss" scoped>
.logo-preview {
  display: inline-block;
  margin-bottom: $space-xxs;
  position: relative;

  .logo-img {
    background-color: $color-black-light;
    border: 2px solid $color-black;
    height: 72px;
    object-fit: contain;
    width: 72px;
  }
}

$media-gap: $space-xxs;
.gym-media {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-bottom: $space-xxs;
  gap: $media-gap;
}

.gym-image {
  --columns: 5;
  --gap-count: calc( var(--columns) - 1 );
  aspect-ratio: 1 / 1;
  flex-basis: calc( calc( 100% / var(--columns) ) - calc( $media-gap / var(--columns) * var(--gap-count) ) );
  position: relative;

  @include mobile() {
    --columns: 4;
  }

  img, video {
    background-color: $color-black-light;
    border: 2px solid $color-black;
    height: 100%;
    object-fit: contain;
    pointer-events: none;
    width: 100%;
  }
}

.logo-preview .clear-logo,
.gym-image .remove-img {
  cursor: pointer;
  position: absolute;
  top: $space-xxxs;
  right: $space-xxxs;
  padding: 4px;
  width: $space-s;
  height: $space-s;
  background-color: $color-black;
  border-radius: $space-xxs;
}

.gym-image .duration {
  background-color: $color-black;
  bottom: 4px;
  color: $color-white;
  font-family: "Roboto-Bold", $font-base;
  font-size: $size-xxs;
  font-weight: bold;
  line-height: $size-xxs;
  right: 2px;
  padding: 4px;
  position: absolute;
}

.accepted-file-types {
  color: $color-gray-x-dark;
  font-size: $size-xxs;
  font-style: italic;
  margin-bottom: $space-m;
  margin-top: -$space-xs;
}

.social-link {
  margin-bottom: $space-s;
}

.add-social-link {
  .link {
    font-size: $size-xs;
  }
}
</style>
